import useLocale from 'next-translate/useTranslation';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback } from 'react';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';
import { openSiteChat, SITE_CHAT_READY_EVENT } from '@/lib/siteChat';

import Halftone from './Halftone';

/** Entrance delay for `styles.enter`, `styles.bubble` and `styles.rv`. */
export const delay = (ms: number, extra?: Record<string, string>) =>
  ({ '--d': `${ms}ms`, ...extra }) as CSSProperties;

/**
 * Space between two runs of copy, such as a headline and its coloured half.
 * Chinese and Japanese put no space after a full stop.
 */
export function useCopyGap() {
  const { lang } = useLocale();
  return lang === 'ja' || lang === 'zh' ? '' : ' ';
}

/** Shared colour roles, so every section reads as one page. */
export const tone = {
  page: 'bg-[#f8f8fb] dark:bg-[#0b0b13]',
  surface: 'bg-white dark:bg-[#14141f]',
  line: 'ring-1 ring-[#0f0f28]/[0.07] dark:ring-white/[0.08]',
  ink: 'text-[#10101c] dark:text-white',
  body: 'text-[#56566d] dark:text-[#a4a4bd]',
  quiet: 'text-[#6f6f86] dark:text-[#7b7b96]',
} as const;

/** Filled indigo pill: the page's one primary action style. */
export const buttonPrimary =
  'inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 px-6 py-3.5 text-[15px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_30px_-10px_rgba(79,70,229,0.7)] transition-colors duration-200 hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600';

/** Quiet outlined pill for the secondary action next to it. */
export const buttonSecondary =
  'inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-[#10101c] ring-1 ring-[#0f0f28]/10 transition duration-200 hover:ring-[#0f0f28]/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-white/[0.06] dark:text-white dark:ring-white/15 dark:hover:ring-white/30';

type SectionHeadingProps = {
  title: ReactNode;
  /** `md` suits headlines that share their row with another column. */
  size?: 'lg' | 'md';
  /** Muted continuation of the headline, on the same line of thought. */
  tail?: ReactNode;
  lead?: ReactNode;
  align?: 'left' | 'center';
  invert?: boolean;
  className?: string;
  id?: string;
};

/** Two-tone headline: the claim in ink, its payoff in a quieter grey. */
export function SectionHeading({
  title,
  tail,
  lead,
  size = 'lg',
  align = 'left',
  invert = false,
  className,
  id,
}: SectionHeadingProps) {
  const gap = useCopyGap();

  return (
    <div
      className={clsxm(
        'max-w-3xl',
        align === 'center' && 'mx-auto text-center',
        className,
      )}
    >
      <h2
        id={id}
        className={clsxm(
          styles.display,
          'text-[2.125rem] font-semibold leading-[1.08] sm:leading-[1.06]',
          size === 'lg'
            ? 'sm:text-5xl lg:text-[3.5rem]'
            : 'sm:text-[2.5rem] lg:text-[2.75rem]',
          invert ? 'text-white' : tone.ink,
        )}
      >
        {title}
        {tail ? (
          <>
            {gap}
            <span
              className={
                invert ? 'text-[#9d9dc0]' : 'text-[#8a8aa1] dark:text-[#6e6e8c]'
              }
            >
              {tail}
            </span>
          </>
        ) : null}
      </h2>
      {lead ? (
        <p
          className={clsxm(
            'mt-6 max-w-[38rem] text-lg leading-8',
            align === 'center' && 'mx-auto',
            invert ? 'text-[#b7b7d2]' : tone.body,
          )}
        >
          {lead}
        </p>
      ) : null}
    </div>
  );
}

type StageProps = {
  variant?: 'indigo' | 'lavender' | 'ink';
  className?: string;
  style?: CSSProperties;
  /** Focal point of the halftone. */
  halftoneAt?: string;
  /** How far the halftone reaches; keep it short on stages that hold text. */
  halftoneReach?: number;
  children?: ReactNode;
};

/**
 * The coloured surface product UI sits on. Indigo and ink stages carry the
 * page's accent moments; lavender is the quiet everyday stage.
 */
export function Stage({
  variant = 'lavender',
  className,
  halftoneAt = '100% 0%',
  halftoneReach,
  style,
  children,
}: StageProps) {
  const surface = {
    indigo:
      'bg-[linear-gradient(155deg,rgb(var(--tw-color-primary-500))_0%,rgb(var(--tw-color-primary-700))_100%)] ring-1 ring-inset ring-white/10 dark:bg-[linear-gradient(155deg,rgb(var(--tw-color-primary-600))_0%,rgb(var(--tw-color-primary-800))_100%)]',
    lavender:
      'bg-[#eeeefc] ring-1 ring-inset ring-[#0f0f28]/[0.04] dark:bg-[#15142a] dark:ring-white/[0.06]',
    ink: 'bg-[#0d0c1f] ring-1 ring-inset ring-white/[0.07]',
  }[variant];

  const dots = {
    indigo: 'opacity-[0.3] [--ht:rgb(var(--tw-color-primary-300))]',
    lavender:
      'opacity-50 [--ht:rgb(var(--tw-color-primary-300))] dark:opacity-40 dark:[--ht:rgb(var(--tw-color-primary-800))]',
    ink: 'opacity-40 [--ht:rgb(var(--tw-color-primary-700))]',
  }[variant];

  return (
    <div
      className={clsxm(
        'relative isolate overflow-hidden rounded-[28px] sm:rounded-[36px]',
        surface,
        className,
      )}
      style={style}
    >
      <Halftone
        at={halftoneAt}
        reach={halftoneReach}
        className={clsxm('-z-10', dots)}
      />
      {children}
    </div>
  );
}

/**
 * Opens the site's own support agent (the chat bubble mounted by
 * SiteChatWidget). The launcher loads after the page settles, so a click
 * that arrives earlier waits for it instead of doing nothing.
 */
export function useOpenAgentDemo() {
  return useCallback(() => {
    if (openSiteChat()) return;
    const onReady = () => {
      window.removeEventListener(SITE_CHAT_READY_EVENT, onReady);
      openSiteChat();
    };
    window.addEventListener(SITE_CHAT_READY_EVENT, onReady);
    // Older launchers have no API: a click on the bubble opens it.
    document.getElementById('dialoq-btn')?.click();
  }, []);
}

export function CheckGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={clsxm('h-4 w-4 flex-none', className)}
    >
      <path
        fillRule='evenodd'
        d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z'
        clipRule='evenodd'
      />
    </svg>
  );
}

export function ArrowGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
      className={clsxm('h-4 w-4 flex-none rtl:-scale-x-100', className)}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M4 10h12m0 0l-5-5m5 5l-5 5'
      />
    </svg>
  );
}
